import { fetchLastStates } from '@/services/states-api.service'
import { logger } from '@/utils/logger'

/**
 * State types that indicate Dayforce sync is complete.
 * Sync is considered complete when all these state types have `finishedAt` set.
 */
const DAYFORCE_SYNC_STATE_TYPES = ['sync', 'sync_people', 'fetch_dayforce_data']

const STORAGE_KEY = 'dayforce-sync-boardId'

/**
 * Check if all required sync states have completed
 */
function checkSyncComplete(statesList) {
  if (!statesList || statesList.length === 0) {
    return false
  }

  const statesByType = {}
  for (const state of statesList) {
    statesByType[state.type] = state
  }

  return DAYFORCE_SYNC_STATE_TYPES.every((type) => {
    const state = statesByType[type]
    return state && state.finishedAt != null
  })
}

/**
 * Check if sync is in progress (states exist but not all finished)
 */
function checkSyncInProgress(statesList) {
  if (!statesList || statesList.length === 0) {
    return false
  }

  // Sync is in progress if at least one state exists with startedAt but not all have finishedAt
  const hasStarted = statesList.some((s) => s.startedAt != null)
  const allFinished = checkSyncComplete(statesList)

  return hasStarted && !allFinished
}

/**
 * Save boardId to localStorage for sync recovery after page refresh
 */
export function saveSyncBoardId(boardId) {
  try {
    localStorage.setItem(STORAGE_KEY, boardId)
  } catch (e) {
    logger.warn('[Dayforce Sync] Failed to save boardId to localStorage:', e.message)
  }
}

/**
 * Get saved boardId from localStorage
 */
export function getSavedSyncBoardId() {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch (e) {
    return null
  }
}

/**
 * Clear saved boardId from localStorage
 */
export function clearSyncBoardId() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    // ignore
  }
}

/**
 * Check current sync status for a board
 * @returns {{ isComplete: boolean, isInProgress: boolean }}
 */
export async function checkSyncStatus(boardId) {
  try {
    const states = await fetchLastStates({ boardId })
    return {
      isComplete: checkSyncComplete(states),
      isInProgress: checkSyncInProgress(states)
    }
  } catch (err) {
    logger.error('[Dayforce Sync] Error checking sync status:', err.message)
    return { isComplete: false, isInProgress: false }
  }
}

/**
 * Wait for Dayforce sync to complete by polling backend states.
 * Polls indefinitely until sync completes.
 *
 * @param {string} boardId - The board ID to check states for
 * @param {Object} options
 * @param {number} options.pollInterval - Polling interval in ms (default: 3000)
 * @returns {Promise<boolean>} - Resolves to true when sync is complete
 */
export async function waitForDayforceSync(boardId, options = {}) {
  const { pollInterval = 3000 } = options

  let isComplete = false
  while (!isComplete) {
    try {
      const states = await fetchLastStates({ boardId })
      if (checkSyncComplete(states)) {
        clearSyncBoardId()
        isComplete = true
        return true
      }
    } catch (err) {
      logger.error('[Dayforce Sync] Error fetching states:', err.message)
      // Continue polling even on error, might be temporary network issue
    }

    if (!isComplete) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval))
    }
  }
  return true
}
